import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { default as api, type ShopItem } from '../service/api';
import { createPersistStorage } from './utils/persist';

interface ManagerShopState {
  items: ShopItem[];
  isLoading: boolean;
  error: string | null;
}

interface ManagerShopActions {
  fetchShopItems: () => Promise<void>;
  deleteShopItem: (itemId: string) => Promise<void>;
  reorderItems: (reorderedItems: ShopItem[]) => Promise<void>;
  clearShopItems: () => void;
}

type ManagerShopStore = ManagerShopState & ManagerShopActions;

type ManagerShopStorePersist = Pick<ManagerShopStore, 'items'>;

const useManagerShopStore = create<ManagerShopStore>()(
  persist<ManagerShopStore, [], [], ManagerShopStorePersist>(
    (set, get) => ({
      items: [],
      isLoading: false,
      error: null,

      fetchShopItems: async () => {
        const hasCachedItems = get().items.length > 0;
        set({ isLoading: !hasCachedItems, error: null });

        try {
          const items = await api.shopItemsGet();
          set({
            items,
            isLoading: false,
          });
        } catch (error) {
          set({
            error:
              error instanceof Error
                ? error.message
                : 'Failed to fetch shop items',
            isLoading: false,
          });
        }
      },

      deleteShopItem: async (itemId: string) => {
        try {
          await api.shopItemDelete(itemId);
          // Remove item from state
          set(state => ({
            items: state.items.filter(item => item._id !== itemId),
          }));
        } catch (error) {
          throw new Error(
            error instanceof Error
              ? error.message
              : 'Failed to delete shop item',
          );
        }
      },

      reorderItems: async (reorderedItems: ShopItem[]) => {
        // Optimistically update the UI
        set({ items: reorderedItems });

        try {
          const payload = reorderedItems.map((item, index) => ({
            id: item._id,
            position: index,
          }));
          await api.shopItemsReorder(payload);
        } catch (error) {
          // Revert on failure by re-fetching
          await get().fetchShopItems();
          throw new Error(
            error instanceof Error
              ? error.message
              : 'Failed to reorder shop items',
          );
        }
      },

      clearShopItems: () =>
        set({
          items: [],
          error: null,
        }),
    }),
    {
      name: 'manager-shop-store',
      storage: createPersistStorage<ManagerShopStorePersist>(),
      partialize: state => ({
        items: state.items,
      }),
    },
  ),
);

export default useManagerShopStore;
